import React, { useState } from "react";
import { FaExternalLinkAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./Projects.css";

const Projects = () => {
  const [expanded, setExpanded] = useState({});

  const ProjectLinkButton = ({ projectId }) => {
    const navigate = useNavigate();
    return (
      <button
        className="project-link"
        aria-label={`Open ${projectId} details`}
        onClick={(e) => {
          e.stopPropagation();
          // ensure viewport is at top immediately before navigating
          try {
            window.scrollTo({ top: 0, left: 0 });
          } catch (err) {
            window.scrollTo(0, 0);
          }
          navigate(`/projects/${projectId}`);
        }}
      >
        <FaExternalLinkAlt />
      </button>
    );
  };

  const toggleReadMore = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const projects = [
    {
      id: 1,
      title: "HackElite",
      description:
        "HackElite is a nationwide coding competition crafted to test and elevate the problem-solving skills of undergraduates. It combines intense coding rounds with workshops led by industry professionals, focusing on both technical excellence and professional development. Participants collaborate, innovate, and compete to create impactful tech solutions.",
      image: "/assets/2025updated/Hackelite.jpg",
      technologies: ["Technology", "Innovation", "Collaboration", "Workshops"],
      githubLink: "#",
      liveLink: "#",
    },
    {
      id: 3,
      title: "NurtureNook",
      description:
        "NurtureNook is a compassionate outreach initiative that supports young children from underprivileged communities. Through the distribution of essential school supplies and the hosting of joyful, interactive learning sessions, the program creates a nurturing environment that encourages both academic interest and emotional well-being.",
      image: "/assets/2025updated/Nurturenook.jpg",
      technologies: [
        "Community Service",
        "Education",
        "Child Welfare",
        "Interactive Activities",
      ],
      githubLink: "#",
      liveLink: "#",
    },
    {
      id: 4,
      title: "Futuro",
      description:
        "Futuro is an inspiring career guidance event tailored for A/L Science students. It connects them with experienced professionals and academics from STEM fields through engaging workshops and motivational sessions. By bridging classroom learning with industry insights, Futuro empowers students to navigate their futures with clarity and enthusiasm.",
      image: "/assets/2025/Futuro.jpeg",
      technologies: [
        "STEM",
        "Career Guidance",
        "Workshops",
        "Motivational Talks",
      ],
      githubLink: "#",
      liveLink: "#",
    },
    {
      id: 5,
      title: "Hervolution",
      description:
        "Hervolution is a blog initiative that offers a platform for female undergraduates at the University of Moratuwa to share technical knowledge, experiences, and personal insights. Published bi-weekly on Medium, the series encourages ongoing reflection, learning, and community engagement throughout the academic year.",
      image: "/assets/2025/Hervolution.jpg",
      technologies: [
        "Blogging",
        "Content Creation",
        "Knowledge Sharing",
        "Medium",
      ],
      githubLink: "#",
      liveLink: "#",
    },
    {
      id: 6,
      title: "Radiance",
      description:
        "Radiance is the Annual General Meeting and Award Ceremony of IEEE WIE UoM. It honors outstanding volunteer contributions and highlights the year’s milestones. The event cultivates a culture of appreciation, inspires continued leadership, and strengthens bonds within the WIE community.",
      image: "/assets/2025updated/Radiance.jpg",
      technologies: [
        "AGM",
        "Awards Ceremony",
        "Community Building",
        "Recognition",
      ],
      githubLink: "#",
      liveLink: "#",
    },
    {
      id: 7,
      title: "FestX",
      description:
        "FestX marked the inaugural celebration of the IEEE WIE Affinity Group at University of Moratuwa. The event featured technical competitions such as ModelX (Blender) and WebX (portfolio building), paired with workshops to upskill participants and celebrate the start of an impactful journey.",
      image: "/assets/2025updated/FestX12025.JPG",
      technologies: [
        "Inauguration",
        "Competitions",
        "Workshops",
        "Technical Skills",
      ],
      githubLink: "#",
      liveLink: "#",
    },
    {
      id: 8,
      title: "Enginuity",
      description:
        "Enginuity is a monthly panel discussion series that highlights the journeys and achievements of women engineers across diverse industries. By sharing real-life stories and career advice, the series fosters empowerment, builds connections, and promotes visibility for women in STEM fields.",
      image: "/assets/2025updated/Enginuity12025.jpg",
      technologies: [
        "Empowerment",
        "Panel Discussions",
        "Women in Engineering",
        "Networking",
      ],
      githubLink: "#",
      liveLink: "#",
    },
    {
      id: 9,
      title: "EmpowerHer",
      description:
        "EmpowerHer is a webinar organized to celebrate International Women's Day, focused on empowering participants to build strong, authentic personal brands. Featuring inspiring speakers and interactive discussions, the event equipped attendees with practical tools to grow in their careers, boost self-confidence, and stand out in a competitive world.",
      image: "/assets/2025updated/EmpowerHer12025.jpeg",
      technologies: [
        "Webinar",
        "Personal Branding",
        "Women's Day",
        "Inspiration",
      ],
      githubLink: "#",
      liveLink: "#",
    },
  ];

  return (
    <section id="projects" className="section projects">
      <div className="container">
        <h2 className="section-title">Our Projects</h2>
        <p className="section-subtitle">
          Innovative solutions created by our talented members to address
          real-world challenges
        </p>

        <div className="projects-grid">
          {projects.map((project, index) => (
            <div
              key={project.id}
              className={`project-card animate-fade-in-up`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="project-image">
                <img src={project.image} alt={project.title} />
                <div className="project-overlay">
                  <div className="project-links">
                    {/* navigate programmatically to avoid anchor/scroll issues */}
                    <ProjectLinkButton projectId={project.id} />
                  </div>
                </div>
              </div>

              <div className="project-content">
                <h3 className="project-title">{project.title}</h3>
                <p className="project-description">
                  {expanded[project.id] || project.description.length <= 200
                    ? project.description
                    : project.description.slice(0, 150) + "..."}
                  {project.description.length > 150 && (
                    <button
                      className="read-more-btn"
                      onClick={() => toggleReadMore(project.id)}
                    >
                      {expanded[project.id] ? " Read Less" : " Read More"}
                    </button>
                  )}
                </p>

                <div className="project-technologies">
                  {project.technologies.map((tech, techIndex) => (
                    <span key={techIndex} className="tech-tag">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
